import React from 'react';
import { Banknote, ShieldCheck, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { PaymentMethodBadges, DIRECT_METHOD_LABELS } from './PaymentMethodBadges';
import { customerOfferPricing, feeLabel, eur } from '../utils/offerPricing';

const Row = ({ label, value, hint, strong, tone }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-black/5 last:border-0">
    <div className="min-w-0 flex-1">
      <p className={strong ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-600'}>{label}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
    <span className={`shrink-0 tabular-nums whitespace-nowrap ${strong ? 'text-base font-extrabold' : 'text-sm font-medium'} ${
      tone === 'green' ? 'text-[#1B5E20]' : tone === 'amber' ? 'text-amber-700' : 'text-gray-800'
    }`}>{value}</span>
  </div>
);

/**
 * Secure your delivery — what the customer pays now vs directly to the driver.
 */
export default function PaymentMethodChoiceDialog({ open, offer, onCancel, onChoose, processing }) {
  if (!offer) return null;
  const { bookingFee, payNow, payDriver, total } = customerOfferPricing(offer);
  const onlineNow = payNow;
  const methods = offer.driver_payment_methods || ['cash'];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="payment-choice-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <Banknote className="w-5 h-5 text-amber-600" /> Secure your delivery
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            Your total delivery price includes a {feeLabel(bookingFee)} JustDeliver.ie booking fee.
            Pay the booking fee and any applicable 10% booking charge now to secure your booking.
            Pay the remaining delivery balance directly to your driver.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3" data-testid="payment-choice-breakdown">
          <Row
            label="Total delivery price"
            value={eur(total)}
            hint={`Includes ${feeLabel(bookingFee)} JustDeliver.ie booking fee`}
            strong
          />
          <Row label="Pay now to secure your booking" value={eur(payNow)} strong tone="green" />
          <Row label="Pay your driver directly" value={eur(payDriver)} strong tone="amber" />
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3">
          <p className="text-xs font-semibold text-amber-900 mb-1.5">Your driver accepts</p>
          <PaymentMethodBadges methods={methods} size="md" dataTestId="payment-choice-driver-methods" />
          <p className="text-[11px] text-amber-800 mt-2 leading-snug">
            The {eur(payDriver)} is paid <strong>directly to your driver</strong> using one of these methods —
            JustDeliver does not process or hold that part of the payment.
          </p>
        </div>

        <div className="flex items-start gap-2 text-[11px] text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#2E7D32]" />
          <span>
            Your driver's contact details unlock as soon as your {eur(onlineNow)} online payment is confirmed.
            Keep your 6-digit completion code until the delivery is done.
          </span>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={processing} data-testid="payment-choice-cancel">
            Back
          </Button>
          <Button
            className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-2"
            onClick={() => onChoose('direct')}
            disabled={processing}
            data-testid="payment-choice-direct"
          >
            {processing ? 'Processing…' : <>Pay {eur(onlineNow)} now <ArrowRight className="w-4 h-4" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DIRECT_METHOD_LABELS };
